"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import type { EmailAskKind } from "./use-announce-email-ask"
import type { PendingEchoLine } from "./use-echo-launcher-lines"
import type { MarkerBreakdownData } from "./marker-breakdown"
import type { PricingCardWire } from "./pricing-breakdown"

export interface ChatTurn {
  /**
   * Stable identity for this turn, assigned by `appendTurn` (and backfilled at
   * hydration for turns persisted before ids existed). It exists so a caller
   * can patch THE turn it appended rather than "the last turn": the Chatwoot
   * subscription appends agent replies mid-stream, so "last" can change hands
   * while deltas are still arriving — patching by position wrote a bot answer
   * into an agent's turn and left the real bot turn streaming forever
   * (conv 132, 2026-07-31).
   */
  id: string
  role: "user" | "bot" | "human" | "system"
  content: string
  ts: string
  isStreaming?: boolean
  agentName?: string
  /**
   * The human agent's Chatwoot profile photo for this turn ("" / undefined when
   * they haven't set one). Only ever populated on `human` turns — rendered as
   * the avatar beside their reply. See AgentAvatar.
   */
  agentAvatarUrl?: string
  isCrisis?: boolean
  /**
   * Backend message id for a bot turn (from the `message_meta` SSE event). Equals
   * the Langfuse trace id — retained on the turn for trace correlation.
   */
  messageId?: string
  /**
   * Knowledge-base pages the bot used to answer this turn, from the `sources`
   * SSE event. Rendered as a small "Sources" row beneath the bot message.
   * Pre-sanitized server-side (host-allowlisted, deduped, capped 0–4).
   */
  sources?: ChatSource[]
  /**
   * Files/images attached to this turn. Inbound from Chatwoot (agent sends a
   * screenshot) or echoed for a visitor upload. The render path is live; the
   * data path is dormant until the backend starts populating it (AHD-7851).
   */
  attachments?: ChatAttachment[]
  /**
   * Renders the one-time "not medical advice" notice beneath this bot turn
   * (from the `medical_topic` SSE event — the model flagged the turn as
   * medical territory). At most one turn per conversation carries this:
   * "already shown" is derived from the transcript, and the flag persists
   * with it (AHD-8753).
   */
  medicalNotice?: boolean
  /**
   * Target locale for a one-click "switch language?" offer on a system turn,
   * from the `suggest_locale_switch` SSE event. Already validated against the
   * current country's languages, so it is safe to build a URL from (see
   * lib/locale-switch.ts). The bot answers in the page locale regardless — this
   * only ever offers; the customer decides.
   */
  localeSwitch?: string
  /**
   * Deterministic biomarker-list breakdown for this bot turn (from the
   * `marker_breakdown` SSE event). Rendered as the MarkerBreakdown card beneath
   * the prose — the resolver's projected facts, never model-generated, so the
   * inclusion display can't hallucinate (see marker-breakdown.tsx).
   */
  markerBreakdown?: MarkerBreakdownData
  /**
   * Deterministic package/price card for this bot turn (`pricing_card` SSE
   * event, AHD-11297). glue emits the authoritative package prices; the widget
   * builds the full card (blood test + add-ons from its own feed). Prices are
   * never model-generated, so the card can't misquote (see pricing-breakdown.tsx).
   */
  pricingCard?: PricingCardWire
  /**
   * Follow-up suggestion chips for this bot turn (from the `suggested_questions`
   * SSE event). Rendered as a tappable row beneath the latest bot answer; a tap
   * sends the chip text as the next user message. `followupSource` distinguishes
   * LLM-generated chips from the static medical-safe set, for analytics.
   */
  followups?: ChatFollowup[]
  followupSource?: "generated" | "static"
}

export interface ChatSource {
  label: string
  url?: string
}

export interface ChatFollowup {
  text: string
  /** Provenance for analytics: `followup.<scope>.<i>` or `followup.static.<i>`. */
  key: string
}

export interface ChatAttachment {
  id: string
  kind: "image" | "file"
  url: string
  name?: string
  mime?: string
  sizeBytes?: number
}

export interface ChatSessionState {
  conversation_id: number | null
  source_id: string | null
  pubsub_token: string | null
  turns: ChatTurn[]
  panel_open: boolean
  teaser_dismissed: boolean
  /** Inbox opts into collecting a visitor email (from the `session` event). */
  email_collect_enabled: boolean
  /** Visitor has already supplied their email, so don't ask again. */
  email_provided: boolean
  /** Milliseconds to wait after a handoff before offering an optional email
   *  (from the `session` event). 0 disables it. */
  slow_reply_prompt_ms: number
  /**
   * Epoch ms when the conversation was handed to a human, or null when nothing
   * is pending — either no handoff yet, or a human has since replied.
   *
   * Lives here rather than in ChatPanel because ChatPanel unmounts the moment
   * the panel is collapsed (and on every route change), and "handed off, then
   * collapsed the panel and waited" is precisely the case the slow-reply email
   * ask exists for. Panel-local state would lose the instant and the ask could
   * never fire.
   */
  handoff_at: number | null
  /** Visitor dismissed the slow-reply email ask. Persisted alongside
   *  `handoff_at` so collapsing and reopening the panel doesn't resurrect a
   *  prompt they already said no to. */
  slow_reply_dismissed: boolean
  /**
   * Email asks already reported to the backend (`/api/chat/email-ask`), so the
   * agent timeline gets one note per ask rather than one per render.
   *
   * Persisted rather than kept in a ref because the ask forms live inside
   * ChatPanel, which unmounts whenever the panel is collapsed or the route
   * changes — a ref would forget and re-report on every reopen. Scoped to this
   * tab's session, so a second tab can report the same ask once more; that is
   * accurate (it was shown again) and bounded by glue's rate limit.
   */
  email_asks_announced: EmailAskKind[]
  /**
   * Highest Chatwoot message id this transcript already accounts for — whether
   * it was rendered or deliberately dropped as the bot's own echo.
   *
   * Persisted so a reopened panel can ask Chatwoot for just what it missed
   * instead of the whole history. It narrows the query — it isn't what keeps the
   * catch-up honest; content matching (`isBotEcho`) does that.
   */
  last_chatwoot_message_id: number | null
  /**
   * Client-generated launcher lines (the Ella greeting and friends) the widget
   * showed the visitor before a Chatwoot conversation existed, waiting to be
   * echoed into the conversation once its id is known.
   *
   * Persisted rather than kept in a ref because the greeting fires ~700ms after
   * the panel opens — before the visitor's first message creates the
   * conversation — and ChatPanel unmounts whenever the panel is collapsed. A ref
   * would forget a line buffered before the first message, so the greeting would
   * never reach the agent transcript. Drained the moment the flush starts.
   */
  pending_echo_lines: PendingEchoLine[]
}

/**
 * How much of the session may be written to the device.
 *
 * `functional` is the consented tier — the full blob. `necessary` is the
 * visitor who clicked our launcher without granting the `functional` category:
 * ePrivacy Art. 5(3) exempts storage that is strictly necessary for a service
 * the visitor explicitly requested, and their click requested a chat. It does
 * not exempt anything beyond that chat. See
 * docs/superpowers/specs/2026-07-30-chat-launcher-always-visible-design.md.
 */
export type ChatStorageTier = "necessary" | "functional"

/**
 * Which tier each field of the session belongs to. Exhaustive over
 * `ChatSessionState` on purpose: adding a field to the state is a type error
 * here until it has been classified, so the compliance boundary can't be
 * widened by accident.
 *
 * `necessary` covers the conversation the visitor asked for and the state
 * needed to keep it alive across a collapsed panel or an SPA navigation —
 * without it the requested service is simply broken. That includes the
 * transcript: `ChatPanel` unmounts the moment the panel closes, so a
 * non-persisted transcript is a lost conversation, not a privacy win.
 *
 * `functional` is what makes chat nicer rather than possible. Today that's just
 * the teaser: a proactive nudge nobody asked for, and the unconsented path
 * doesn't show it at all (see `ContextualChat`), so there is nothing to
 * remember either.
 */
const FIELD_TIER: Record<keyof ChatSessionState, ChatStorageTier> = {
  conversation_id: "necessary",
  source_id: "necessary",
  pubsub_token: "necessary",
  turns: "necessary",
  panel_open: "necessary",
  last_chatwoot_message_id: "necessary",
  pending_echo_lines: "necessary",
  handoff_at: "necessary",
  slow_reply_dismissed: "necessary",
  slow_reply_prompt_ms: "necessary",
  email_collect_enabled: "necessary",
  email_provided: "necessary",
  email_asks_announced: "necessary",
  teaser_dismissed: "functional",
}

/**
 * Strips the consent-tier fields before a `necessary`-tier write. They stay in
 * the in-memory state — the tier limits what lands on the device, not what the
 * mounted widget can do while the visitor is on the page.
 */
function projectForTier(
  s: ChatSessionState,
  tier: ChatStorageTier
): ChatSessionState {
  if (tier === "functional") return s
  const next = { ...s }
  const empty = emptyState()
  const keys = Object.keys(FIELD_TIER) as (keyof ChatSessionState)[]
  keys.forEach((key) => {
    if (FIELD_TIER[key] === "necessary") return
    // Reset to the default rather than deleting the key, so the persisted blob
    // stays a well-formed ChatSessionState for the next reader.
    Object.assign(next, { [key]: empty[key] })
  })
  return next
}

/**
 * Session storage is scoped by `country` + `locale` so switching the
 * site's language never drags German turns into an English panel (and
 * vice versa). Each locale pair gets its own independent conversation
 * — a deliberate trade: starting a new chat when the user switches
 * language is clearer than trying to hand a half-German transcript
 * over to the bot in English.
 */
function storageKeyFor(country: string, locale: string): string {
  return `ahead-chat-session:${country}:${locale}`
}

// Legacy (pre-split) key — read once at hydration so users already
// mid-session at the moment this ships don't lose their conversation.
// After the first successful read we delete the legacy blob.
const LEGACY_STORAGE_KEY = "ahead-chat-session"

function newTurnId(): string {
  return crypto.randomUUID()
}

/**
 * Heals turns coming out of storage — applied only at hydration, never to
 * live turns. A `ChatTurn.isStreaming: true` was mid-answer at the moment
 * this device last wrote the session; there is no live stream to finish it
 * after a reload (or a reopen following a closed panel), so left alone it
 * rendered a permanently stuck "Thinking…" bubble.
 *
 * - Every rehydrated turn loses its (necessarily stale) `isStreaming` flag.
 * - A bot turn that never produced any text (interrupted before its first
 *   token) is dropped rather than rendered as an empty finalized bubble. A
 *   bot turn with partial content is kept — it becomes a finalized partial
 *   answer, which is better than losing it outright.
 */
export function healHydratedTurns(turns: ChatTurn[]): ChatTurn[] {
  return turns
    .filter((t) => t.role !== "bot" || t.content.trim() !== "")
    .map((t) => (t.isStreaming ? { ...t, isStreaming: false } : t))
}

function readStorage(storageKey: string): ChatSessionState {
  if (typeof window === "undefined") return emptyState()
  try {
    const raw =
      sessionStorage.getItem(storageKey) ??
      sessionStorage.getItem(LEGACY_STORAGE_KEY)
    if (!raw) return emptyState()
    // Clean up the legacy blob once we've lifted from it.
    sessionStorage.removeItem(LEGACY_STORAGE_KEY)
    const stored = {
      ...emptyState(),
      ...(JSON.parse(raw) as Partial<ChatSessionState>),
    }
    // Turns persisted before ids existed get one here, so `updateTurn` /
    // `dropTurn` can rely on every hydrated turn carrying an id.
    return {
      ...stored,
      turns: healHydratedTurns(
        stored.turns.map((t) => ({ ...t, id: t.id ?? newTurnId() }))
      ),
    }
  } catch {
    return emptyState()
  }
}

function emptyState(): ChatSessionState {
  return {
    conversation_id: null,
    source_id: null,
    pubsub_token: null,
    turns: [],
    panel_open: false,
    teaser_dismissed: false,
    email_collect_enabled: false,
    email_provided: false,
    slow_reply_prompt_ms: 0,
    handoff_at: null,
    slow_reply_dismissed: false,
    email_asks_announced: [],
    last_chatwoot_message_id: null,
    pending_echo_lines: [],
  }
}

function writeStorage(
  storageKey: string,
  s: ChatSessionState,
  tier: ChatStorageTier
): void {
  if (typeof window === "undefined") return
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(projectForTier(s, tier)))
  } catch {
    // storage full / blocked — ignore
  }
}

/**
 * Hook signature is args-optional so existing tests that don't care
 * about locale scoping (e.g. unit tests of turn append/update
 * behaviour) keep working. Real callers on a page should always pass
 * `{ country, locale }`.
 */
interface UseChatSessionArgs {
  country: string
  locale: string
  /**
   * Defaults to `functional` — the tier every caller ran at before this
   * existed, so nothing changes unless it's passed. `ContextualChat` passes
   * `necessary` for the click-booted, unconsented visitor.
   */
  storageTier?: ChatStorageTier
}

export function useChatSession(args?: UseChatSessionArgs) {
  const storageKey = args
    ? storageKeyFor(args.country, args.locale)
    : LEGACY_STORAGE_KEY
  // Keep the current key in a ref so callbacks see fresh values even
  // if the caller remounts the hook under a different locale.
  const storageKeyRef = useRef(storageKey)
  storageKeyRef.current = storageKey

  // Same trick for the tier: consent can be granted while the widget is
  // mounted, and the very next write must honour the new tier without waiting
  // for a remount or re-creating every callback.
  const tierRef = useRef<ChatStorageTier>(args?.storageTier ?? "functional")
  tierRef.current = args?.storageTier ?? "functional"

  const [session, setSession] = useState<ChatSessionState>(emptyState)
  // Hydration happens in an effect (sessionStorage isn't available
  // during SSR), so the first client render is always `emptyState`.
  // Consumers that branch on "is the session empty?" — e.g. the
  // welcome flow in ChatPanel — must wait for `hydrated === true`,
  // otherwise they'd treat a not-yet-rehydrated session as fresh and
  // re-fire on every panel reopen.
  const [hydrated, setHydrated] = useState(false)

  // Hydrate on mount, and re-hydrate whenever the locale/country
  // changes so a language switch loads the right transcript (or a
  // fresh empty one).
  useEffect(() => {
    setSession(readStorage(storageKey))
    setHydrated(true)
  }, [storageKey])

  const persist = useCallback(
    (updater: (prev: ChatSessionState) => ChatSessionState) => {
      setSession((prev) => {
        const next = updater(prev)
        writeStorage(storageKeyRef.current, next, tierRef.current)
        return next
      })
    },
    []
  )

  const setConversation = useCallback(
    (c: {
      conversation_id: number
      source_id: string
      pubsub_token: string
      email_collect?: boolean
      slow_reply_prompt_ms?: number
    }) => {
      const { email_collect, slow_reply_prompt_ms, ...ids } = c
      persist((p) => ({
        ...p,
        ...ids,
        email_collect_enabled: email_collect ?? p.email_collect_enabled,
        slow_reply_prompt_ms: slow_reply_prompt_ms ?? p.slow_reply_prompt_ms,
      }))
    },
    [persist]
  )

  const setEmailProvided = useCallback(() => {
    persist((p) => ({ ...p, email_provided: true }))
  }, [persist])

  /**
   * Records when the conversation was handed to a human.
   *
   * `keepExisting` is for the recovery path (glue's `handoff_active`, emitted
   * whenever the visitor messages an already-handed-off conversation): the
   * handoff happened at some earlier, unknown instant, so a stored value is
   * always better than "now" — resetting the clock there would push the
   * slow-reply ask a full threshold further away on every message.
   */
  const noteHandoff = useCallback(
    (at: number, keepExisting = false) => {
      persist((p) =>
        keepExisting && p.handoff_at !== null ? p : { ...p, handoff_at: at }
      )
    },
    [persist]
  )

  /** A human replied, so nothing is waiting any more. */
  const clearHandoff = useCallback(() => {
    persist((p) => (p.handoff_at === null ? p : { ...p, handoff_at: null }))
  }, [persist])

  const dismissSlowReply = useCallback(() => {
    persist((p) => ({ ...p, slow_reply_dismissed: true }))
  }, [persist])

  /** Marks an email ask as reported. Idempotent — the list stays a set. */
  const noteEmailAskAnnounced = useCallback(
    (kind: EmailAskKind) => {
      persist((p) =>
        p.email_asks_announced.includes(kind)
          ? p
          : { ...p, email_asks_announced: [...p.email_asks_announced, kind] }
      )
    },
    [persist]
  )

  /** Appends a turn and returns its generated id, so the caller can patch or
   *  drop THAT turn later — never "the last one", which can change hands while
   *  a stream is in flight (see the ChatTurn.id doc). */
  const appendTurn = useCallback(
    (t: Omit<ChatTurn, "id">) => {
      const id = newTurnId()
      persist((p) => ({ ...p, turns: [...p.turns, { ...t, id }] }))
      return id
    },
    [persist]
  )

  /**
   * Inserts turns at the FRONT of the transcript — used to hydrate the prior
   * history glue replays when it stitches a fresh tab onto an existing
   * conversation (AHD-10752 `history` event). Submit has already appended this
   * turn's user + streaming-bot turns, so the prior turns belong ahead of them.
   * Each gets a fresh id like `appendTurn`; the caller guards it to fire once.
   */
  const prependTurns = useCallback(
    (newTurns: Omit<ChatTurn, "id">[]) => {
      if (newTurns.length === 0) return
      persist((p) => ({
        ...p,
        turns: [
          ...newTurns.map((t) => ({ ...t, id: newTurnId() })),
          ...p.turns,
        ],
      }))
    },
    [persist]
  )

  /** Patches the turn with this id. A miss is a no-op — the turn may have been
   *  legitimately dropped (empty-bubble take-back) before a late patch lands. */
  const updateTurn = useCallback(
    (id: string, patch: Partial<Omit<ChatTurn, "id">>) => {
      persist((p) => {
        const index = p.turns.findIndex((turn) => turn.id === id)
        if (index === -1) return p
        const next = [...p.turns]
        next[index] = { ...next[index], ...patch }
        return { ...p, turns: next }
      })
    },
    [persist]
  )

  /**
   * Removes the turn with this id — used to take back the empty bot bubble left
   * behind when a turn produced nothing at all. The predicate keeps that
   * narrow: it must still hold at removal time (the bubble may have been filled
   * since the caller decided to drop it).
   */
  const dropTurn = useCallback(
    (id: string, predicate?: (turn: ChatTurn) => boolean) => {
      persist((p) => {
        const turn = p.turns.find((t) => t.id === id)
        if (!turn) return p
        if (predicate && !predicate(turn)) return p
        return { ...p, turns: p.turns.filter((t) => t.id !== id) }
      })
    },
    [persist]
  )

  /**
   * Removes the last turn if the predicate holds — the restored-transcript
   * variant of `dropTurn`, for callers that only know "the transcript ended on
   * an empty bot bubble" (catch-up after a remount) rather than a turn id.
   */
  const dropLastTurn = useCallback(
    (predicate?: (turn: ChatTurn) => boolean) => {
      persist((p) => {
        const last = p.turns[p.turns.length - 1]
        if (!last) return p
        if (predicate && !predicate(last)) return p
        return { ...p, turns: p.turns.slice(0, -1) }
      })
    },
    [persist]
  )

  /** Advances the Chatwoot high-water mark. Monotonic — never moves backwards. */
  const noteChatwootMessage = useCallback(
    (id: number) => {
      persist((p) =>
        p.last_chatwoot_message_id !== null && p.last_chatwoot_message_id >= id
          ? p
          : { ...p, last_chatwoot_message_id: id }
      )
    },
    [persist]
  )

  /** Buffers a launcher line to echo once the conversation id is known. */
  const bufferEchoLine = useCallback(
    (line: PendingEchoLine) => {
      persist((p) => ({
        ...p,
        pending_echo_lines: [...p.pending_echo_lines, line],
      }))
    },
    [persist]
  )

  /** Clears the buffered launcher lines — called as the flush starts, so a
   *  concurrent remount can't flush them a second time. */
  const drainEchoLines = useCallback(() => {
    persist((p) =>
      p.pending_echo_lines.length === 0 ? p : { ...p, pending_echo_lines: [] }
    )
  }, [persist])

  const setPanelOpen = useCallback(
    (open: boolean) => {
      persist((p) => ({ ...p, panel_open: open }))
    },
    [persist]
  )

  const dismissTeaser = useCallback(() => {
    persist((p) => ({ ...p, teaser_dismissed: true }))
  }, [persist])

  const reset = useCallback(() => {
    persist(() => emptyState())
  }, [persist])

  return {
    session,
    hydrated,
    setConversation,
    setEmailProvided,
    noteHandoff,
    clearHandoff,
    dismissSlowReply,
    noteEmailAskAnnounced,
    appendTurn,
    prependTurns,
    updateTurn,
    dropTurn,
    dropLastTurn,
    noteChatwootMessage,
    bufferEchoLine,
    drainEchoLines,
    setPanelOpen,
    dismissTeaser,
    reset,
  }
}

export type ChatSessionHandle = ReturnType<typeof useChatSession>
